import React, { useState, useEffect } from 'react';
import { 
  ShieldCheck, 
  LayoutDashboard, 
  Building2, 
  Users, 
  ShieldAlert, 
  Plus, 
  Search, 
  CheckCircle2, 
  XCircle, 
  Loader2, 
  ArrowRight, 
  LogOut, 
  Globe,
  TrendingUp,
  Mail,
  User,
  Hash,
  ChevronRight,
  BookOpen,
  UserCircle2,
  PhoneCall,
  Clock,
  ArrowLeft,
  Trash2,
  Play,
  Pause,
  KeyRound,
  AlertTriangle,
  Database,
  Archive,
  Download,
  RotateCcw,
  Smartphone,
  Upload,
  Send,
  Bell,
  FileText
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import apiClient from '@/utils/apiClient';
import downloadFile from '@/utils/fileDownloader';
import { toast } from 'sonner';

const SuperAdminPortal = () => {
  const [step, setStep] = useState(1); // 1: Identity, 2: OTP, 3: Dashboard
  const [loading, setLoading] = useState(false);
  const [schools, setSchools] = useState([]);
  const [authData, setAuthData] = useState({ username: '', email: '', otp: '' });
  const [isAuthorized, setIsAuthorized] = useState(false);
  
  // School Detail State
  const [selectedSchool, setSelectedSchool] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  // School Generation State
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSchool, setNewSchool] = useState({ name: '', city_code: '' });
  const [gmailStatus, setGmailStatus] = useState('checking');

  // Pause / Resume / Delete & Reset Password State
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [pauseNote, setPauseNote] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmName, setDeleteConfirmName] = useState('');
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');

  // Delete Backup Flow State
  const [backupVerified, setBackupVerified] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [backupFilePath, setBackupFilePath] = useState('');

  // Mobile Control State
  const [mobileReleases, setMobileReleases] = useState([]);
  const [mobileReleasesLoading, setMobileReleasesLoading] = useState(false);
  const [uploadingRelease, setUploadingRelease] = useState(false);
  const [newReleaseVersion, setNewReleaseVersion] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  const [selectedReleaseFile, setSelectedReleaseFile] = useState(null);

  const [notificationTitle, setNotificationTitle] = useState('');
  const [notificationBody, setNotificationBody] = useState('');
  const [notificationTargetGroup, setNotificationTargetGroup] = useState('all');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [notificationLogs, setNotificationLogs] = useState([]);

  useEffect(() => {
    const storedUser = localStorage.getItem('hostelconnect_user');
    if (storedUser) {
      const user = JSON.parse(storedUser);
      if (user.role === 'super_admin') {
        setIsAuthorized(true);
        setStep(3);
        fetchSchools();
        fetchBackups();
        fetchMobileReleases();
        fetchNotificationLogs();
        checkGmailHealth();
      }
    }
  }, []);

  const fetchMobileReleases = async () => {
    setMobileReleasesLoading(true);
    try {
      const response = await apiClient.get('/v1/super-admin/mobile/releases');
      setMobileReleases(response.data);
    } catch (error) {
      console.error('Failed to fetch mobile releases:', error);
    } finally {
      setMobileReleasesLoading(false);
    }
  };

  const fetchNotificationLogs = async () => {
    try {
      const response = await apiClient.get('/v1/super-admin/mobile/notification-logs');
      setNotificationLogs(response.data);
    } catch (error) {
      console.error('Failed to fetch notification logs:', error);
    }
  };

  const handleUploadRelease = async (e) => {
    e.preventDefault();
    if (!selectedReleaseFile || !newReleaseVersion) {
      toast.error('Please select a release file (.apk or .zip) and enter version number.');
      return;
    }
    const fileName = selectedReleaseFile.name.toLowerCase();
    if (!fileName.endsWith('.apk') && !fileName.endsWith('.zip')) {
      toast.error('Only .apk and .zip files are allowed.');
      return;
    }

    setUploadingRelease(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedReleaseFile);
      formData.append('version', newReleaseVersion);
      formData.append('release_notes', releaseNotes);

      const response = await apiClient.post('/v1/super-admin/mobile/upload-release', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success(response.data.message || 'New release uploaded to Cloudflare R2!');
      setNewReleaseVersion('');
      setReleaseNotes('');
      setSelectedReleaseFile(null);
      fetchMobileReleases();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload release');
    } finally {
      setUploadingRelease(false);
    }
  };

  const handleSetActiveRelease = async (releaseId, version) => {
    try {
      const response = await apiClient.post(`/v1/super-admin/mobile/set-active-release/${releaseId}`);
      toast.success(response.data.message || `Set v${version} to ACTIVE`);
      fetchMobileReleases();
    } catch (error) {
      toast.error('Failed to update active release version');
    }
  };

  const handleSendBroadcastNotification = async (e) => {
    e.preventDefault();
    if (!notificationTitle || !notificationBody) {
      toast.error('Please enter notification title and message body.');
      return;
    }

    setSendingNotification(true);
    try {
      const response = await apiClient.post('/v1/super-admin/mobile/send-broadcast-notification', {
        title: notificationTitle,
        body: notificationBody,
        target_group: notificationTargetGroup
      });

      toast.success(response.data.message || 'Push notification sent!');
      setNotificationTitle('');
      setNotificationBody('');
      fetchNotificationLogs();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send push notification');
    } finally {
      setSendingNotification(false);
    }
  };

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/v1/super-admin/dashboard/stats');
      setSchools(response.data);
    } catch (error) {
      toast.error('Failed to fetch school data');
    } finally {
      setLoading(false);
    }
  };

  const checkGmailHealth = async () => {
    try {
      const response = await apiClient.get('/v1/common/health');
      if (response.data?.gmail_status === 'unhealthy') {
        setGmailStatus('unhealthy');
        toast.error('🚨 Gmail API token is expired or invalid. OTP emails will fail!', {
          duration: 10000,
        });
      } else {
        setGmailStatus('healthy');
      }
    } catch (error) {
      setGmailStatus('unknown');
    }
  };

  const fetchSchoolDetails = async (schoolId) => {
    setDetailLoading(true);
    try {
      const response = await apiClient.get(`/v1/super-admin/schools/${schoolId}/details`);
      setSelectedSchool(response.data);
    } catch (error) {
      toast.error('Failed to load school details');
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePauseSchool = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const schoolId = selectedSchool.school_info.school_id;
      await apiClient.post(`/v1/super-admin/schools/${schoolId}/pause`, {
        pause: true,
        note: pauseNote
      });
      toast.success('School access suspended successfully');
      setShowPauseModal(false);
      setPauseNote('');
      await fetchSchoolDetails(schoolId);
      await fetchSchools();
    } catch (error) {
      toast.error('Failed to suspend school');
    } finally {
      setLoading(false);
    }
  };

  const handleResumeSchool = async () => {
    if (!selectedSchool) return;
    setLoading(true);
    try {
      const schoolId = selectedSchool.school_info.school_id;
      await apiClient.post(`/v1/super-admin/schools/${schoolId}/pause`, {
        pause: false
      });
      toast.success('School access resumed successfully');
      await fetchSchoolDetails(schoolId);
      await fetchSchools();
    } catch (error) {
      toast.error('Failed to resume school');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBackup = async () => {
    if (!selectedSchool) return;
    setBackupLoading(true);
    try {
      const schoolId = selectedSchool.school_info.school_id;
      const response = await apiClient.post(`/v1/super-admin/schools/${schoolId}/backup`);
      if (response.data?.success) {
        setBackupVerified(true);
        setBackupFilePath(response.data.backup_file);
        toast.success('Backup successfully generated and verified');
      } else {
        toast.error('Backup generation failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate backup');
    } finally {
      setBackupLoading(false);
    }
  };

  const handleDeleteSchool = async () => {
    if (!selectedSchool) return;
    if (!backupVerified) {
      toast.error('Please generate and verify backup first');
      return;
    }
    if (deleteConfirmName.trim().toLowerCase() !== selectedSchool.school_info.name.trim().toLowerCase()) {
      toast.error('School name confirmation does not match');
      return;
    }
    setLoading(true);
    try {
      const schoolId = selectedSchool.school_info.school_id;
      await apiClient.delete(`/v1/super-admin/schools/${schoolId}`);
      toast.success('School permanently deleted');
      setShowDeleteModal(false);
      setDeleteConfirmName('');
      setBackupVerified(false);
      setBackupFilePath('');
      setSelectedSchool(null);
      await fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete school');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/v1/super-admin/users/reset-password', {
        user_id: resetPasswordUser.id,
        new_password: newPassword
      });
      toast.success(`Password reset successfully for ${resetPasswordUser.name}`);
      setShowResetPasswordModal(false);
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (error) {
      toast.error('Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  const fetchBackups = async () => {
    setBackupsLoading(true);
    try {
      const response = await apiClient.get('/v1/super-admin/backups');
      setBackups(response.data || []);
    } catch (error) {
      toast.error('Failed to load backups archive');
    } finally {
      setBackupsLoading(false);
    }
  };

  const handleDownloadBackup = async (backupId, schoolId) => {
    try {
      const response = await apiClient.get(`/v1/super-admin/backups/${backupId}/download`, {
        responseType: 'blob'
      });
      const mimeType = response.headers?.['content-type'] || 'application/json';
      await downloadFile(response.data, `backup_${schoolId}.json`, mimeType);
    } catch (error) {
      toast.error('Failed to download backup file');
    }
  };

  const handleRestoreSchool = async (backupId) => {
    setLoading(true);
    try {
      await apiClient.post(`/v1/super-admin/backups/${backupId}/restore`);
      toast.success('School successfully restored from backup');
      await fetchBackups();
      await fetchSchools();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore school');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = async () => {
    if (!authData.username || !authData.email) {
      toast.error('Please enter both username and email');
      return;
    }
    setLoading(true);
    try {
      await apiClient.post('/v1/super-admin/auth/request-access', {
        username: authData.username,
        email: authData.email
      });
      toast.success('Verification code sent to your email');
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (!authData.otp) {
      toast.error('Please enter the verification code');
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post('/v1/super-admin/auth/verify-otp', {
        username: authData.username,
        otp: authData.otp
      });
      
      const userData = response.data;
      localStorage.setItem('hostelconnect_user', JSON.stringify(userData.user));
      localStorage.setItem('hostelconnect_token', userData.access_token);
      
      setIsAuthorized(true);
      setStep(3);
      fetchSchools();
      checkGmailHealth();
      toast.success('Central Access Granted');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateSchool = async () => {
    if (!newSchool.name || !newSchool.city_code) {
      toast.error('All fields are required');
      return;
    }
    setLoading(true);
    try {
      const response = await apiClient.post('/v1/super-admin/schools/generate', newSchool);
      toast.success(response.data.message);
      setShowAddModal(false);
      setNewSchool({ name: '', city_code: '' });
      fetchSchools();
    } catch (error) {
      toast.error('Failed to generate school ID');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('hostelconnect_user');
    localStorage.removeItem('hostelconnect_token');
    setIsAuthorized(false);
    setStep(1);
    setAuthData({ username: '', email: '', otp: '' });
  };

  // --- Auth Render Logic (Step 1 & 2 omitted for brevity, same as previous) ---
  if (step === 1) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0c10] p-6 text-slate-100">
        <Card className="w-full max-w-md bg-[#161b22] border-slate-800 shadow-2xl">
          <CardHeader className="text-center pb-2">
            <div className="w-16 h-16 bg-blue-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-blue-500/20">
              <ShieldCheck className="w-10 h-10 text-blue-500" />
            </div>
            <CardTitle className="text-2xl font-black tracking-tight text-white uppercase italic">Central Command</CardTitle>
            <p className="text-slate-500 text-sm font-medium">Verify your administrative identity</p>
          </CardHeader>
          <CardContent className="space-y-4 pt-6">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Username</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white pl-10 h-12 focus:ring-blue-500" 
                  placeholder="Master Username"
                  value={authData.username}
                  onChange={(e) => setAuthData({...authData, username: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Secure Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white pl-10 h-12 focus:ring-blue-500" 
                  placeholder="admin@dormcircle.com"
                  type="email"
                  value={authData.email}
                  onChange={(e) => setAuthData({...authData, email: e.target.value})}
                />
              </div>
            </div>
            <Button 
              className="w-full bg-blue-600 hover:bg-blue-700 h-12 text-white font-black uppercase italic tracking-wider mt-4"
              onClick={handleRequestAccess}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Initialize Verification <ArrowRight className="ml-2 w-4 h-4" /></>}
            </Button>
            <div className="text-center pt-4">
              <p className="text-[10px] text-slate-600 font-bold uppercase tracking-tighter">RESTRICTED AREA: UNAUTHORIZED ACCESS IS LOGGED</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0c10] p-6">
        <Card className="w-full max-w-md bg-[#161b22] border-slate-800 shadow-2xl">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-amber-500/10 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-amber-500/20">
              <ShieldAlert className="w-10 h-10 text-amber-500" />
            </div>
            <CardTitle className="text-2xl font-black tracking-tight text-white uppercase italic">Verification Required</CardTitle>
            <p className="text-slate-500 text-sm font-medium">A code has been sent to {authData.email}</p>
          </CardHeader>
          <CardContent className="space-y-6 pt-4">
            <div className="space-y-1">
              <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest ml-1">Authentication Code</label>
              <div className="relative">
                <Hash className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white pl-10 h-14 text-2xl font-black tracking-[0.5em] text-center" 
                  maxLength={6}
                  value={authData.otp}
                  onChange={(e) => setAuthData({...authData, otp: e.target.value})}
                />
              </div>
            </div>
            <Button 
              className="w-full bg-amber-600 hover:bg-amber-700 h-12 text-white font-black uppercase italic tracking-wider"
              onClick={handleVerifyOTP}
              disabled={loading}
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Verify Identity</>}
            </Button>
            <button 
              className="w-full text-slate-500 text-xs font-bold uppercase hover:text-slate-300"
              onClick={() => setStep(1)}
            >
              Cancel & Go Back
            </button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // --- Render Dashboard ---
  return (
    <div className="min-h-screen bg-[#0d1117] text-slate-300">
      <nav className="h-16 border-b border-slate-800 bg-[#161b22] px-8 flex items-center justify-between sticky top-0 z-50">
        <div className="flex items-center gap-4">
          <div className="bg-blue-600 p-1.5 rounded-lg shadow-lg shadow-blue-500/20">
            <ShieldCheck className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-black text-white tracking-tighter uppercase italic">Central Command</h1>
            <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest leading-none">System Oversight Portal</p>
          </div>
        </div>
        
        <div className="flex items-center gap-6">
          {gmailStatus === 'unhealthy' && (
            <div className="hidden md:flex items-center gap-3 px-4 py-1.5 bg-rose-500/10 rounded-full border border-rose-500/30 text-rose-500">
              <div className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-pulse"></div>
              <span className="text-xs font-black uppercase italic">Gmail API Offline</span>
            </div>
          )}
          <div className="hidden md:flex items-center gap-3 px-4 py-1.5 bg-[#0d1117] rounded-full border border-slate-800">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse"></div>
            <span className="text-xs font-black text-slate-400 uppercase italic">Server: Online</span>
          </div>
          <Button 
            variant="ghost" 
            className="text-slate-400 hover:text-white hover:bg-slate-800"
            onClick={handleLogout}
          >
            <LogOut className="w-4 h-4 mr-2" />
            <span className="font-bold uppercase text-xs">Logout</span>
          </Button>
        </div>
      </nav>

      <main className="p-8 max-w-7xl mx-auto space-y-8">
        
        {/* Statistics Header */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="bg-[#161b22] border-slate-800 hover:border-blue-500/30 transition-all group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Schools</p>
                  <h3 className="text-3xl font-black text-white">{schools.length}</h3>
                </div>
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center group-hover:bg-blue-500/20">
                  <Building2 className="w-6 h-6 text-blue-500" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-[#161b22] border-slate-800 hover:border-emerald-500/30 transition-all group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Total Students</p>
                  <h3 className="text-3xl font-black text-white">
                    {schools.reduce((acc, s) => acc + s.students_count, 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center group-hover:bg-emerald-500/20">
                  <Users className="w-6 h-6 text-emerald-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#161b22] border-slate-800 hover:border-purple-500/30 transition-all group">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">Active Staff</p>
                  <h3 className="text-3xl font-black text-white">
                    {schools.reduce((acc, s) => acc + s.wardens_count, 0)}
                  </h3>
                </div>
                <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center group-hover:bg-purple-500/20">
                  <TrendingUp className="w-6 h-6 text-purple-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="flex items-center">
            <Button 
              className="w-full h-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase italic text-lg shadow-xl shadow-blue-900/20"
              onClick={() => setShowAddModal(true)}
            >
              <Plus className="w-6 h-6 mr-2" />
              New ID
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="flex gap-2 border-b border-slate-800 pb-px">
          <button
            onClick={() => setActiveTab('schools')}
            className={`px-6 py-3 font-bold text-xs uppercase tracking-wider border-b-2 transition-all ${
              activeTab === 'schools'
                ? 'border-blue-500 text-white font-black'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            Active Institutions
          </button>
          <button
            onClick={() => {
              setActiveTab('backups');
              fetchBackups();
            }}
            className={`px-6 py-3 font-bold text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'backups'
                ? 'border-blue-500 text-white font-black'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            Security & Backup Archive
            {backups.length > 0 && (
              <span className="bg-slate-800 text-slate-300 text-[10px] px-2 py-0.5 rounded-full font-black">
                {backups.length}
              </span>
            )}
          </button>
          <button
            onClick={() => {
              setActiveTab('mobile');
              fetchMobileReleases();
              fetchNotificationLogs();
            }}
            className={`px-6 py-3 font-bold text-xs uppercase tracking-wider border-b-2 transition-all flex items-center gap-2 ${
              activeTab === 'mobile'
                ? 'border-blue-500 text-white font-black'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            <Smartphone className="w-4 h-4 text-blue-500" />
            Mobile App Control
            {mobileReleases.length > 0 && (
              <span className="bg-blue-500/20 text-blue-400 text-[10px] px-2 py-0.5 rounded-full font-black border border-blue-500/30">
                v{mobileReleases.find(r => r.is_active)?.version || '1.0.0'}
              </span>
            )}
          </button>
        </div>

        {activeTab === 'schools' && (
          <Card className="bg-[#161b22] border-slate-800 overflow-hidden">
            <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg font-black text-white uppercase italic">Institution Inventory</CardTitle>
                <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Click any school for deep-dive report</p>
              </div>
              <div className="relative w-64">
                 <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-600" />
                 <Input className="bg-[#0d1117] border-slate-800 pl-10 h-10 text-xs text-white" placeholder="Search by ID or Name..." />
              </div>
            </CardHeader>
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-[#0d1117] text-[10px] font-black text-slate-500 uppercase tracking-widest">
                    <th className="px-6 py-4 border-b border-slate-800">School Name & ID</th>
                    <th className="px-6 py-4 border-b border-slate-800">Status</th>
                    <th className="px-6 py-4 border-b border-slate-800">Capacity</th>
                    <th className="px-6 py-4 border-b border-slate-800">Staff</th>
                    <th className="px-6 py-4 border-b border-slate-800">Registered</th>
                    <th className="px-6 py-4 border-b border-slate-800 text-right">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {schools.map((school) => (
                    <tr 
                      key={school.school_id} 
                      className="group hover:bg-slate-900/40 cursor-pointer transition-colors"
                      onClick={() => fetchSchoolDetails(school.school_id)}
                    >
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-slate-850 rounded-xl flex items-center justify-center border border-slate-850 group-hover:border-blue-500/20 group-hover:bg-blue-500/5 transition-all">
                            <Building2 className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                          </div>
                          <div>
                            <div className="text-sm font-black text-white group-hover:text-blue-400 transition-colors">{school.name}</div>
                            <div className="text-[10px] font-bold text-slate-500 font-mono">{school.school_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                         {school.paused ? (
                           <span className="text-[9px] font-black bg-rose-500/10 text-rose-500 px-2.5 py-1 rounded-full border border-rose-500/20 uppercase tracking-wider animate-pulse">
                             Suspended
                           </span>
                         ) : school.is_setup ? (
                           <span className="text-[9px] font-black bg-emerald-500/10 text-emerald-500 px-2.5 py-1 rounded-full border border-emerald-500/20 uppercase tracking-wider">
                             Active
                           </span>
                         ) : (
                           <span className="text-[9px] font-black bg-amber-500/10 text-amber-500 px-2.5 py-1 rounded-full border border-amber-500/20 uppercase tracking-wider">
                             Pending Setup
                           </span>
                         )}
                      </td>
                      <td className="px-6 py-5">
                        <span className="text-lg font-black text-slate-300">{school.students_count}</span>
                        <span className="text-[10px] text-slate-600 ml-1 uppercase font-bold">Students</span>
                      </td>
                      <td className="px-6 py-5 font-bold text-slate-400">{school.wardens_count}</td>
                      <td className="px-6 py-5 text-xs text-slate-500">{school.created_at.split('T')[0]}</td>
                      <td className="px-6 py-5 text-right">
                         <ChevronRight className="w-5 h-5 text-slate-700 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {activeTab === 'backups' && (
          <Card className="bg-[#161b22] border-slate-800 overflow-hidden animate-in fade-in duration-300">
            <CardHeader className="border-b border-slate-800">
              <CardTitle className="text-lg font-black text-white uppercase italic flex items-center gap-2">
                <Database className="w-5 h-5 text-blue-500" />
                Backups & Recovery Archive
              </CardTitle>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                Recover deleted institutions or download their data snapshots
              </p>
            </CardHeader>
            <div className="overflow-x-auto">
              {backupsLoading ? (
                <div className="flex flex-col items-center justify-center p-12 space-y-4">
                  <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
                  <span className="text-xs text-slate-500 font-bold uppercase tracking-widest animate-pulse">Loading archive...</span>
                </div>
              ) : backups.length === 0 ? (
                <div className="flex flex-col items-center justify-center p-16 space-y-3 text-center">
                  <Archive className="w-12 h-12 text-slate-700" />
                  <span className="text-sm text-slate-500 font-bold uppercase tracking-wider">No backups archived</span>
                  <p className="text-xs text-slate-600 max-w-xs font-semibold uppercase tracking-wide">
                    Deleted schools with completed security backups will appear here.
                  </p>
                </div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="bg-[#0d1117] text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      <th className="px-6 py-4 border-b border-slate-800">School Details</th>
                      <th className="px-6 py-4 border-b border-slate-800">Backup Timestamp</th>
                      <th className="px-6 py-4 border-b border-slate-800 text-center">Snapshot Stats</th>
                      <th className="px-6 py-4 border-b border-slate-800 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {backups.map((b) => (
                      <tr key={b.id} className="hover:bg-slate-900/40 border-b border-slate-800/50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-slate-800 rounded-lg flex items-center justify-center border border-slate-700">
                              <Building2 className="w-4 h-4 text-slate-400" />
                            </div>
                            <div>
                              <div className="text-sm font-black text-white">{b.school_name}</div>
                              <div className="text-[10px] font-bold text-slate-500 font-mono">{b.school_id}</div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-xs font-bold text-slate-400">
                          {new Date(b.backup_timestamp).toLocaleString(undefined, {
                            dateStyle: 'medium',
                            timeStyle: 'short'
                          })}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-3">
                            <span className="text-[10px] font-bold bg-slate-800/80 text-slate-400 px-2.5 py-1 rounded-md border border-slate-700/50 uppercase tracking-wider">
                              Hostels: {b.hostels_count}
                            </span>
                            <span className="text-[10px] font-bold bg-slate-800/80 text-slate-400 px-2.5 py-1 rounded-md border border-slate-700/50 uppercase tracking-wider">
                              Students: {b.students_count}
                            </span>
                            <span className="text-[10px] font-bold bg-slate-800/80 text-slate-400 px-2.5 py-1 rounded-md border border-slate-700/50 uppercase tracking-wider">
                              Staff: {b.users_count}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              onClick={() => handleDownloadBackup(b.id, b.school_id)}
                              variant="ghost"
                              className="text-slate-400 hover:text-white hover:bg-slate-800 border border-slate-800 h-9 px-3 gap-1.5"
                            >
                              <Download className="w-3.5 h-3.5" />
                              <span className="text-[10px] font-bold uppercase">JSON</span>
                            </Button>
                            <Button
                              onClick={() => handleRestoreSchool(b.id)}
                              className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-3 gap-1.5 shadow-lg shadow-emerald-500/10"
                            >
                              <RotateCcw className="w-3.5 h-3.5" />
                              <span className="text-[10px] font-black uppercase">Restore</span>
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </Card>
        )}

        {activeTab === 'mobile' && (
          <div className="space-y-8 animate-in fade-in duration-300">
            {/* Section 1: Cloudflare R2 OTA Build Publisher */}
            <Card className="bg-[#161b22] border-slate-800 overflow-hidden">
              <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-black text-white uppercase italic flex items-center gap-2">
                    <Upload className="w-5 h-5 text-blue-500" />
                    Over-The-Air (OTA) Build Publisher
                  </CardTitle>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                    Upload React build.zip to Cloudflare R2 for instant mobile app deployment
                  </p>
                </div>
                <div className="px-3 py-1 bg-blue-500/10 border border-blue-500/30 rounded-full flex items-center gap-2">
                  <Globe className="w-3.5 h-3.5 text-blue-400" />
                  <span className="text-[10px] font-black text-blue-400 uppercase">Cloudflare R2 Storage</span>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                <form onSubmit={handleUploadRelease} className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Release Package (.apk / .zip)</label>
                    <div className="border-2 border-dashed border-slate-800 hover:border-blue-500/50 bg-[#0d1117] rounded-xl p-4 text-center cursor-pointer transition-all">
                      <input
                        type="file"
                        accept=".apk,.zip"
                        onChange={(e) => setSelectedReleaseFile(e.target.files[0])}
                        className="hidden"
                        id="zip-upload-input"
                      />
                      <label htmlFor="zip-upload-input" className="cursor-pointer block space-y-2">
                        <FileText className="w-8 h-8 text-blue-500 mx-auto" />
                        <span className="text-xs font-bold text-slate-300 block truncate">
                          {selectedReleaseFile ? selectedReleaseFile.name : 'Click to select .apk or .zip'}
                        </span>
                        <span className="text-[10px] text-slate-500 uppercase font-semibold block">
                          {selectedReleaseFile ? `${(selectedReleaseFile.size / (1024 * 1024)).toFixed(2)} MB` : 'Android APK or React bundle zip'}
                        </span>
                      </label>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Release Version</label>
                      <Input
                        className="bg-[#0d1117] border-slate-800 text-white text-xs h-10 font-mono"
                        placeholder="e.g. 1.0.1"
                        value={newReleaseVersion}
                        onChange={(e) => setNewReleaseVersion(e.target.value)}
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Release Notes</label>
                      <Input
                        className="bg-[#0d1117] border-slate-800 text-white text-xs h-10"
                        placeholder="e.g. New attendance feature & UI bug fixes"
                        value={releaseNotes}
                        onChange={(e) => setReleaseNotes(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex items-end">
                    <Button
                      type="submit"
                      disabled={uploadingRelease || !selectedReleaseFile}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase italic h-12 text-sm shadow-lg shadow-blue-500/20"
                    >
                      {uploadingRelease ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Uploading to R2...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Publish Build to R2
                        </>
                      )}
                    </Button>
                  </div>
                </form>

                {/* Release History Table */}
                <div className="pt-4 border-t border-slate-800 space-y-3">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Cloudflare R2 Release History</h4>
                  {mobileReleasesLoading ? (
                    <div className="flex justify-center p-6">
                      <Loader2 className="w-6 h-6 text-blue-500 animate-spin" />
                    </div>
                  ) : mobileReleases.length === 0 ? (
                    <p className="text-xs text-slate-600 font-bold uppercase italic py-4">No mobile builds published yet.</p>
                  ) : (
                    <div className="overflow-x-auto rounded-lg border border-slate-800">
                      <table className="w-full text-left border-collapse">
                        <thead>
                          <tr className="bg-[#0d1117] text-[10px] font-black text-slate-500 uppercase tracking-widest">
                            <th className="px-4 py-3 border-b border-slate-800">Version</th>
                            <th className="px-4 py-3 border-b border-slate-800">Status</th>
                            <th className="px-4 py-3 border-b border-slate-800">Release Notes</th>
                            <th className="px-4 py-3 border-b border-slate-800">Created At</th>
                            <th className="px-4 py-3 border-b border-slate-800 text-right">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50 text-xs">
                          {mobileReleases.map((rel) => (
                            <tr key={rel.id} className="hover:bg-slate-900/40">
                              <td className="px-4 py-3 font-mono font-black text-white">v{rel.version}</td>
                              <td className="px-4 py-3">
                                {rel.is_active ? (
                                  <span className="text-[9px] font-black bg-emerald-500/10 text-emerald-400 px-2 py-0.5 rounded-full border border-emerald-500/30 uppercase">
                                    Active (Live)
                                  </span>
                                ) : (
                                  <span className="text-[9px] font-bold bg-slate-800 text-slate-500 px-2 py-0.5 rounded-full uppercase">
                                    Archived
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-slate-400">{rel.release_notes || 'Initial Release'}</td>
                              <td className="px-4 py-3 text-slate-500 font-mono text-[11px]">
                                {new Date(rel.created_at).toLocaleString()}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-2">
                                  {rel.download_url && (
                                    <a
                                      href={rel.download_url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded text-[10px] font-bold uppercase border border-slate-700"
                                    >
                                      Download Zip
                                    </a>
                                  )}
                                  {!rel.is_active && (
                                    <Button
                                      onClick={() => handleSetActiveRelease(rel.id, rel.version)}
                                      size="sm"
                                      className="bg-blue-600 hover:bg-blue-700 text-white text-[10px] font-black uppercase h-7 px-2.5"
                                    >
                                      Set Active
                                    </Button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Section 2: Command Center Push Notification Broadcast */}
            <Card className="bg-[#161b22] border-slate-800 overflow-hidden">
              <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-black text-white uppercase italic flex items-center gap-2">
                    <Bell className="w-5 h-5 text-amber-500" />
                    Broadcast Push Notification Center
                  </CardTitle>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                    Send instant Firebase FCM push alerts to parents, wardens, or admins
                  </p>
                </div>
              </CardHeader>
              <CardContent className="p-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                <form onSubmit={handleSendBroadcastNotification} className="space-y-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Target Audience</label>
                    <select
                      value={notificationTargetGroup}
                      onChange={(e) => setNotificationTargetGroup(e.target.value)}
                      className="w-full bg-[#0d1117] border border-slate-800 text-white text-xs h-10 rounded-md px-3 font-bold"
                    >
                      <option value="all">📢 All Registered App Users</option>
                      <option value="parent">👨‍👩‍👧 Parents Only</option>
                      <option value="warden">🛡️ Wardens Only</option>
                      <option value="admin">🏢 School Admins</option>
                    </select>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Notification Title</label>
                    <Input
                      className="bg-[#0d1117] border-slate-800 text-white text-xs h-10 font-bold"
                      placeholder="e.g. 📢 Important School Announcement"
                      value={notificationTitle}
                      onChange={(e) => setNotificationTitle(e.target.value)}
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Message Body</label>
                    <textarea
                      rows={3}
                      className="w-full bg-[#0d1117] border border-slate-800 text-white text-xs rounded-md p-3 font-medium focus:ring-amber-500"
                      placeholder="e.g. Mid-term examination schedules and weekly reports are now available in your parent app portal."
                      value={notificationBody}
                      onChange={(e) => setNotificationBody(e.target.value)}
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={sendingNotification || !notificationTitle || !notificationBody}
                    className="w-full bg-amber-600 hover:bg-amber-700 text-white font-black uppercase italic h-11 text-xs shadow-lg shadow-amber-500/20"
                  >
                    {sendingNotification ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Broadcasting FCM Alert...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Send Push Notification Now
                      </>
                    )}
                  </Button>
                </form>

                {/* Notification Logs History */}
                <div className="space-y-3">
                  <h4 className="text-xs font-black text-slate-400 uppercase tracking-widest">Recent Broadcast Logs</h4>
                  {notificationLogs.length === 0 ? (
                    <p className="text-xs text-slate-600 font-bold uppercase italic py-4">No broadcast push notifications sent yet.</p>
                  ) : (
                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-1">
                      {notificationLogs.map((log) => (
                        <div key={log.id} className="p-3 bg-[#0d1117] border border-slate-800 rounded-lg space-y-1">
                          <div className="flex items-center justify-between text-xs">
                            <span className="font-black text-white">{log.title}</span>
                            <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded border border-emerald-500/20">
                              Sent: {log.successful_sent} devices
                            </span>
                          </div>
                          <p className="text-[11px] text-slate-400">{log.body}</p>
                          <div className="flex items-center justify-between text-[10px] text-slate-500 font-mono pt-1">
                            <span>Target: {log.target_group.toUpperCase()}</span>
                            <span>{new Date(log.created_at).toLocaleString()}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* --- Detailed Report Overlay --- */}
      {selectedSchool && (
        <div className="fixed inset-0 bg-[#0a0c10]/95 backdrop-blur-md z-[100] flex flex-col animate-in fade-in zoom-in duration-300 overflow-y-auto">
          <header className="h-20 border-b border-slate-800 flex items-center justify-between px-8 bg-[#161b22] sticky top-0 z-10">
            <div className="flex items-center gap-6">
              <Button 
                variant="ghost" 
                className="text-slate-500 hover:text-white"
                onClick={() => setSelectedSchool(null)}
              >
                <ArrowLeft className="w-5 h-5 mr-2" />
                Back to Inventory
              </Button>
              <div className="h-8 w-px bg-slate-800"></div>
              <div>
                <h2 className="text-2xl font-black text-white uppercase italic tracking-tight">{selectedSchool.school_info.name}</h2>
                <p className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em]">{selectedSchool.school_info.school_id} • FULL PERFORMANCE AUDIT</p>
              </div>
            </div>
            <div className="flex gap-4 items-center">
              {selectedSchool.school_info.paused && (
                <div className="flex flex-col text-right">
                  <span className="text-xs font-black text-rose-500 uppercase tracking-widest">SUSPENDED</span>
                  <span className="text-[10px] text-slate-500 max-w-[200px] truncate" title={selectedSchool.school_info.pause_reason}>
                    Reason: {selectedSchool.school_info.pause_reason || "None"}
                  </span>
                </div>
              )}
              {selectedSchool.school_info.paused ? (
                <Button 
                  onClick={handleResumeSchool} 
                  className="bg-emerald-600 hover:bg-emerald-700 text-white font-black uppercase italic text-xs h-10 px-4 gap-1.5"
                  disabled={loading}
                >
                  <Play className="w-4 h-4" />
                  Resume Access
                </Button>
              ) : (
                <Button 
                  onClick={() => setShowPauseModal(true)} 
                  className="bg-amber-600 hover:bg-amber-700 text-white font-black uppercase italic text-xs h-10 px-4 gap-1.5"
                  disabled={loading}
                >
                  <Pause className="w-4 h-4" />
                  Suspend Access
                </Button>
              )}
              <Button 
                onClick={() => setShowDeleteModal(true)} 
                className="bg-rose-600 hover:bg-rose-700 text-white font-black uppercase italic text-xs h-10 px-4 gap-1.5"
                disabled={loading}
              >
                <Trash2 className="w-4 h-4" />
                Delete School
              </Button>
              <div className="bg-slate-900 px-4 py-2 rounded-xl border border-slate-800 flex items-center gap-3 h-10">
                 <Clock className="w-4 h-4 text-slate-500" />
                 <span className="text-xs font-bold text-slate-400 uppercase italic">Registered: {new Date(selectedSchool.school_info.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </header>

          <div className="p-8 max-w-7xl mx-auto w-full space-y-8 pb-20">
            {/* Top Level Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
               {[
                 { label: 'Total Students', value: selectedSchool.stats.total_students, icon: Users, color: 'text-blue-500' },
                 { label: 'Registered Parents', value: selectedSchool.stats.total_parents, icon: UserCircle2, color: 'text-purple-500' },
                 { label: 'Active Staff', value: selectedSchool.stats.total_wardens, icon: ShieldCheck, color: 'text-emerald-500' },
                 { label: 'Managed Hostels', value: selectedSchool.stats.total_hostels, icon: Building2, color: 'text-amber-500' }
               ].map((stat, i) => (
                 <Card key={i} className="bg-[#161b22] border-slate-800 p-6 flex items-center justify-between">
                    <div>
                      <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest mb-1">{stat.label}</p>
                      <p className="text-3xl font-black text-white italic">{stat.value}</p>
                    </div>
                    <stat.icon className={`w-8 h-8 ${stat.color} opacity-50`} />
                 </Card>
               ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Class-wise Breakdown */}
              <div className="lg:col-span-1">
                <Card className="bg-[#161b22] border-slate-800 h-full">
                  <CardHeader className="border-b border-slate-800">
                    <CardTitle className="text-sm font-black text-white uppercase italic flex items-center gap-2">
                      <BookOpen className="w-4 h-4 text-blue-500" />
                      Class-wise Distribution
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="divide-y divide-slate-800">
                      {Object.entries(selectedSchool.class_distribution).map(([className, count], i) => (
                        <div key={i} className="flex items-center justify-between p-4 hover:bg-slate-800/50">
                          <span className="text-sm font-bold text-slate-300 uppercase tracking-tight">Class {className}</span>
                          <div className="flex items-center gap-3">
                            <span className="text-lg font-black text-white italic">{count}</span>
                            <div className="w-16 bg-slate-900 h-1.5 rounded-full overflow-hidden">
                              <div 
                                className="bg-blue-600 h-full rounded-full" 
                                style={{ width: `${(count / selectedSchool.stats.total_students) * 100}%` }}
                              ></div>
                            </div>
                          </div>
                        </div>
                      ))}
                      {Object.keys(selectedSchool.class_distribution).length === 0 && (
                        <div className="p-10 text-center text-slate-600 text-xs font-bold uppercase italic">No Data Available</div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Student-Parent Registry */}
              <div className="lg:col-span-2">
                <Card className="bg-[#161b22] border-slate-800 h-full">
                  <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
                    <CardTitle className="text-sm font-black text-white uppercase italic flex items-center gap-2">
                      <Users className="w-4 h-4 text-purple-500" />
                      Parent-Student Registry
                    </CardTitle>
                    <span className="text-[10px] font-black text-slate-500 uppercase bg-slate-900 px-2 py-1 rounded border border-slate-800">
                      {selectedSchool.students.length} Entries
                    </span>
                  </CardHeader>
                  <div className="overflow-x-auto max-h-[500px]">
                    <table className="w-full text-left">
                      <thead className="bg-[#0d1117] sticky top-0 z-10">
                        <tr className="text-[9px] font-black text-slate-500 uppercase tracking-widest">
                          <th className="px-6 py-3 border-b border-slate-800">Student</th>
                          <th className="px-6 py-3 border-b border-slate-800">Class</th>
                          <th className="px-6 py-3 border-b border-slate-800">Parent Name</th>
                          <th className="px-6 py-3 border-b border-slate-800">Contact</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-800">
                        {selectedSchool.students.map((stu, i) => (
                          <tr key={i} className="hover:bg-slate-800/30">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center border border-blue-500/20 text-blue-500 font-black text-xs uppercase italic">
                                  {stu.name.charAt(0)}
                                </div>
                                <span className="text-xs font-black text-slate-200 uppercase tracking-tight">{stu.name}</span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                               <span className="text-[10px] font-bold text-slate-500 uppercase bg-slate-900 px-2 py-0.5 rounded border border-slate-800">{stu.class_name}</span>
                            </td>
                            <td className="px-6 py-4 font-bold text-xs text-slate-400 italic">
                                {stu.parent_info?.name}
                            </td>
                            <td className="px-6 py-4">
                               <div className="flex items-center gap-2 text-slate-500 hover:text-blue-400 cursor-pointer transition-colors">
                                  <PhoneCall className="w-3 h-3" />
                                  <span className="text-[10px] font-mono font-bold tracking-widest">{stu.parent_info?.phone}</span>
                               </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </div>
            </div>
            
            {/* System Access & Staff Directory */}
            <Card className="bg-[#161b22] border-slate-800">
              <CardHeader className="border-b border-slate-800 flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-sm font-black text-white uppercase italic flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-emerald-500" />
                    System Access & Staff Directory
                  </CardTitle>
                  <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                    Manage login credentials and system roles for administrative personnel
                  </p>
                </div>
                <span className="text-[10px] font-black text-slate-500 uppercase bg-slate-900 px-2 py-1 rounded border border-slate-800">
                  {((selectedSchool.admins || []).length + (selectedSchool.wardens || []).length)} Accounts
                </span>
              </CardHeader>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="bg-[#0d1117] text-[9px] font-black text-slate-500 uppercase tracking-widest">
                      <th className="px-6 py-3 border-b border-slate-800">Name</th>
                      <th className="px-6 py-3 border-b border-slate-800">System Role</th>
                      <th className="px-6 py-3 border-b border-slate-800">Contact Number</th>
                      <th className="px-6 py-3 border-b border-slate-800 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-800">
                    {[
                      ...(selectedSchool.admins || []).map(a => ({ ...a, displayRole: 'School Administrator', badgeColor: 'bg-blue-500/10 text-blue-400 border-blue-500/20' })),
                      ...(selectedSchool.wardens || []).map(w => ({ ...w, displayRole: 'Hostel Warden', badgeColor: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' }))
                    ].map((user, idx) => (
                      <tr key={idx} className="hover:bg-slate-800/30">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center border border-slate-700 text-slate-300 font-black text-xs uppercase italic">
                              {user.name.charAt(0)}
                            </div>
                            <span className="text-xs font-black text-slate-200 uppercase tracking-tight">{user.name}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`text-[10px] font-black uppercase px-2 py-0.5 rounded border ${user.badgeColor}`}>
                            {user.displayRole}
                          </span>
                        </td>
                        <td className="px-6 py-4 font-mono text-xs text-slate-400">
                          {user.phone}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              setResetPasswordUser(user);
                              setShowResetPasswordModal(true);
                            }}
                            className="bg-slate-900 hover:bg-slate-800 text-slate-400 hover:text-white border border-slate-800 hover:border-slate-700 text-xs font-bold uppercase italic px-3 py-1.5 h-8 gap-1.5 inline-flex items-center"
                          >
                            <KeyRound className="w-3.5 h-3.5" />
                            Reset Password
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {((selectedSchool.admins || []).length === 0 && (selectedSchool.wardens || []).length === 0) && (
                      <tr>
                        <td colSpan={4} className="p-10 text-center text-slate-600 text-xs font-bold uppercase italic">
                          No System User Accounts Found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Suspend Access Modal */}
      {showPauseModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <Card className="w-full max-w-md bg-[#161b22] border-slate-800 shadow-2xl">
            <CardHeader>
              <div className="w-12 h-12 bg-amber-500/10 rounded-xl flex items-center justify-center mb-4 border border-amber-500/20">
                <Pause className="w-6 h-6 text-amber-500" />
              </div>
              <CardTitle className="text-xl font-black text-white uppercase italic">
                Suspend Institution Access
              </CardTitle>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                Specify a reason or payment issue note for this suspension
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Suspension Note / Reason</label>
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white" 
                  placeholder="e.g., Pending subscription payment / Code: DC-PAY-501" 
                  value={pauseNote}
                  onChange={(e) => setPauseNote(e.target.value)}
                />
              </div>
              <div className="flex gap-3 pt-4">
                 <Button className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase text-xs h-12" onClick={() => { setShowPauseModal(false); setPauseNote(''); }}>Cancel</Button>
                 <Button className="flex-1 bg-amber-600 hover:bg-amber-700 text-white font-black uppercase italic text-xs h-12 shadow-lg shadow-amber-500/20" onClick={handlePauseSchool} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Suspension'}
                 </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Permanent Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <Card className="w-full max-w-md bg-[#161b22] border-rose-900/50 shadow-2xl">
            <CardHeader>
              <div className="w-12 h-12 bg-rose-500/10 rounded-xl flex items-center justify-center mb-4 border border-rose-500/20">
                <AlertTriangle className="w-6 h-6 text-rose-500" />
              </div>
              <CardTitle className="text-xl font-black text-white uppercase italic">
                CRITICAL WARNING
              </CardTitle>
              <p className="text-xs text-rose-500/80 font-bold uppercase tracking-widest">
                This action is irreversible and will delete all database records for this institution.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-rose-500/5 border border-rose-500/10 p-4 rounded-xl text-xs text-slate-400 space-y-2">
                <p>Deleting <strong className="text-white">{selectedSchool.school_info.name}</strong> will remove:</p>
                <ul className="list-disc pl-5 space-y-1 font-semibold uppercase text-[10px] text-slate-500 tracking-wider">
                  <li>School access credential</li>
                  <li>All linked Hostel configurations</li>
                  <li>All Warden accounts & permissions</li>
                  <li>All Student records & attendance logs</li>
                  <li>All Parent links</li>
                </ul>
              </div>

              {!backupVerified ? (
                <div className="space-y-3 p-4 bg-slate-900/50 rounded-xl border border-slate-800 animate-in fade-in duration-200">
                  <p className="text-xs font-bold text-slate-400 uppercase tracking-wide">
                    ⚠️ Phase 1: Security Backup Required
                  </p>
                  <p className="text-[10px] text-slate-500 font-medium leading-relaxed">
                    You must generate and verify a full secure backup before this school can be deleted. This ensures data can be recovered if deleted by mistake.
                  </p>
                  <Button 
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-black uppercase italic text-xs h-12 shadow-lg shadow-blue-500/20 gap-2"
                    onClick={handleGenerateBackup}
                    disabled={backupLoading}
                  >
                    {backupLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Generating & Verifying Backup...
                      </>
                    ) : (
                      'Generate & Verify Secure Backup'
                    )}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 animate-in fade-in duration-200">
                  <div className="p-4 bg-emerald-500/5 border border-emerald-500/15 rounded-xl space-y-1 text-xs">
                    <p className="font-black text-emerald-400 uppercase tracking-wider flex items-center gap-1.5">
                      <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                      Secure Backup Verified
                    </p>
                    <p className="text-[10px] text-slate-400 font-mono break-all leading-normal">
                      Saved: {backupFilePath.split('\\').pop() || backupFilePath}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">
                      Type <span className="text-white select-none">"{selectedSchool.school_info.name}"</span> to confirm:
                    </label>
                    <Input 
                      className="bg-[#0d1117] border-slate-800 text-white font-bold" 
                      placeholder="Enter school name" 
                      value={deleteConfirmName}
                      onChange={(e) => setDeleteConfirmName(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-slate-800">
                 <Button 
                   className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase text-xs h-12" 
                   onClick={() => { 
                     setShowDeleteModal(false); 
                     setDeleteConfirmName(''); 
                     setBackupVerified(false); 
                     setBackupFilePath(''); 
                   }}
                 >
                   Cancel
                 </Button>
                 <Button 
                   className="flex-1 bg-rose-600 hover:bg-rose-700 text-white font-black uppercase italic text-xs h-12 shadow-lg shadow-rose-500/20 disabled:opacity-30" 
                   onClick={handleDeleteSchool} 
                   disabled={loading || !backupVerified || deleteConfirmName.trim().toLowerCase() !== selectedSchool.school_info.name.trim().toLowerCase()}
                 >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirm Permanent Deletion'}
                 </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Reset User Password Modal */}
      {showResetPasswordModal && resetPasswordUser && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[110] flex items-center justify-center p-6 animate-in fade-in duration-200">
          <Card className="w-full max-w-md bg-[#161b22] border-slate-800 shadow-2xl">
            <CardHeader>
              <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 border border-blue-500/20">
                <KeyRound className="w-6 h-6 text-blue-500" />
              </div>
              <CardTitle className="text-xl font-black text-white uppercase italic">
                Reset Account Password
              </CardTitle>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">
                Setting new password for {resetPasswordUser.name} ({resetPasswordUser.role})
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">New Secure Password</label>
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white font-mono" 
                  placeholder="Min 6 characters" 
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                />
              </div>
              <div className="flex gap-3 pt-4">
                 <Button className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase text-xs h-12" onClick={() => { setShowResetPasswordModal(false); setResetPasswordUser(null); setNewPassword(''); }}>Cancel</Button>
                 <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase italic text-xs h-12 shadow-lg shadow-blue-500/20" onClick={handleResetPassword} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Password'}
                 </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* --- Add Modal (Omitted same as previous but kept for context) --- */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-6">
          <Card className="w-full max-w-md bg-[#161b22] border-slate-800 shadow-2xl">
            <CardHeader>
              <CardTitle className="text-xl font-black text-white uppercase italic">Generate School Access</CardTitle>
              <p className="text-xs text-slate-500 font-bold uppercase tracking-widest">Create a unique ID for a new institution</p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">Institution Name</label>
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white" 
                  placeholder="e.g. Royal Academy" 
                  value={newSchool.name}
                  onChange={(e) => setNewSchool({...newSchool, name: e.target.value})}
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest">City Code (3 Letters)</label>
                <Input 
                  className="bg-[#0d1117] border-slate-800 text-white uppercase" 
                  placeholder="e.g. GHY, DEL, BOM" 
                  maxLength={3}
                  value={newSchool.city_code}
                  onChange={(e) => setNewSchool({...newSchool, city_code: e.target.value.toUpperCase()})}
                />
              </div>
              <div className="flex gap-3 pt-4">
                 <Button className="flex-1 bg-slate-800 hover:bg-slate-700 text-white font-bold uppercase text-xs h-12" onClick={() => setShowAddModal(false)}>Cancel</Button>
                 <Button className="flex-1 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase italic text-xs h-12 shadow-lg shadow-blue-500/20" onClick={handleGenerateSchool} disabled={loading}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Generate Secure ID'}
                 </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Global Aesthetics */}
      <style dangerouslySetInnerHTML={{ __html: `
        .glass-dark {
          background: rgba(22, 27, 34, 0.8);
          backdrop-filter: blur(12px);
          border: 1px solid rgba(255, 255, 255, 0.1);
        }
        body {
          background-color: #0d1117;
        }
        ::-webkit-scrollbar {
          width: 8px;
        }
        ::-webkit-scrollbar-track {
          background: #0d1117;
        }
        ::-webkit-scrollbar-thumb {
          background: #161b22;
          border-radius: 10px;
        }
        ::-webkit-scrollbar-thumb:hover {
          background: #1e293b;
        }
      `}} />
    </div>
  );
};

export default SuperAdminPortal;
